import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CpmButton, CpmModal } from '../../../../components/design-system';
import { formatCpmCount } from '../../../../constants/centralProductMaster.constants';
import type { CpmExciseCategory } from '../../../../types/centralProductMaster.types';

interface DeleteCategoryConfirmProps {
    open: boolean;
    category: CpmExciseCategory | null;
    stateName: string;
    usageHref: string;
    onClose: () => void;
    onConfirmDelete: () => Promise<void>;
    onDisableInstead: () => Promise<void>;
    busy?: boolean;
}

type Phase = 'confirm' | 'saveError';

export default function DeleteCategoryConfirm({
    open,
    category,
    stateName,
    usageHref,
    onClose,
    onConfirmDelete,
    onDisableInstead,
    busy = false,
}: DeleteCategoryConfirmProps) {
    const [phase, setPhase] = useState<Phase>('confirm');
    const [localBusy, setLocalBusy] = useState(false);
    const [wasBlocked, setWasBlocked] = useState(false);

    useEffect(() => {
        if (open) {
            setPhase('confirm');
            setLocalBusy(false);
            setWasBlocked(false);
        }
    }, [open, category?._id]);

    if (!category) return null;

    const usage = category.usageCount ?? 0;
    const blocked = usage > 0;
    const working = busy || localBusy;
    const title = blocked
        ? `Cannot delete \u201C${category.category}\u201D`
        : `Delete \u201C${category.category}\u201D?`;
    const body = blocked
        ? `Products in ${stateName} are still categorized under this excise category. Deleting it would leave them uncategorized, so the delete is blocked.`
        : `Nothing in ${stateName} references this category. It will be removed from ${stateName}\u2019s taxonomy immediately, along with its subcategories.`;

    const run = async (action: () => Promise<void>, blockedPath: boolean) => {
        setWasBlocked(blockedPath);
        setLocalBusy(true);
        try {
            await action();
            onClose();
        } catch {
            setPhase('saveError');
        } finally {
            setLocalBusy(false);
        }
    };

    const finish = () => {
        if (working) return;
        setPhase('confirm');
        onClose();
    };

    return (
        <CpmModal open={open} onClose={finish} maxWidth={540} zIndex={72}>
            {phase === 'saveError' ? (
                <>
                    <div
                        className="flex items-start"
                        style={{ gap: 14, padding: '22px 24px 16px' }}
                    >
                        <span
                            className="shrink-0 inline-flex items-center justify-center text-[var(--cpm-shortfall-text)]"
                            style={{
                                width: 38,
                                height: 38,
                                borderRadius: 10,
                                background: 'var(--cpm-shortfall-bg)',
                            }}
                        >
                            <svg
                                width="19"
                                height="19"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                <path d="M12 9v4" />
                                <path d="M12 17h.01" />
                            </svg>
                        </span>
                        <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                            <h2 className="m-0 !text-[19px] !font-extrabold !tracking-[-0.02em] !leading-[1.2] !text-[var(--cpm-brand-navy)]">
                                {wasBlocked
                                    ? "Couldn't disable the category"
                                    : "Couldn't delete the category"}
                            </h2>
                            <p className="m-0 !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                                The catalog service did not accept the change. Nothing was removed
                                from {stateName}&apos;s taxonomy. Try again.
                            </p>
                        </div>
                    </div>
                    <div
                        className="flex items-center border-t-[1.5px] border-[var(--cpm-border-strong)]"
                        style={{
                            gap: 10,
                            padding: '18px 24px',
                            background: 'rgba(249,249,252,0.88)',
                        }}
                    >
                        <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                            <CpmButton variant="outline" size="modal" onClick={finish}>
                                Close
                            </CpmButton>
                            <CpmButton
                                variant="navy"
                                size="modal"
                                isLoading={working}
                                onClick={() =>
                                    void run(
                                        wasBlocked ? onDisableInstead : onConfirmDelete,
                                        wasBlocked
                                    )
                                }
                            >
                                Try again
                            </CpmButton>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div
                        className="flex items-start"
                        style={{ gap: 14, padding: '22px 24px 16px' }}
                    >
                        <span
                            className="shrink-0 inline-flex items-center justify-center"
                            style={{
                                width: 38,
                                height: 38,
                                borderRadius: 10,
                                background: blocked
                                    ? 'var(--cpm-warning-bg)'
                                    : 'var(--cpm-shortfall-bg)',
                                color: blocked
                                    ? 'var(--cpm-warning-text)'
                                    : 'var(--cpm-shortfall-text)',
                            }}
                        >
                            {blocked ? (
                                <svg
                                    width="19"
                                    height="19"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                >
                                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                    <path d="M12 9v4" />
                                    <path d="M12 17h.01" />
                                </svg>
                            ) : (
                                <svg
                                    width="19"
                                    height="19"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                >
                                    <path d="M3 6h18" />
                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                    <path d="M10 11v6" />
                                    <path d="M14 11v6" />
                                </svg>
                            )}
                        </span>
                        <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                            <h2 className="m-0 !text-[19px] !font-extrabold !tracking-[-0.02em] !leading-[1.2] !text-[var(--cpm-brand-navy)]">
                                {title}
                            </h2>
                            <p className="m-0 !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                                {body}
                            </p>
                        </div>
                    </div>

                    {blocked ? (
                        <div className="flex flex-col" style={{ margin: '0 24px 4px', gap: 8 }}>
                            <span
                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                style={{
                                    fontSize: 10.5,
                                    fontWeight: 800,
                                    letterSpacing: '0.08em',
                                }}
                            >
                                Still attached
                            </span>
                            <Link
                                to={usageHref}
                                className="!flex !items-center !rounded-[10px] !bg-[var(--cpm-warning-bg)] !border !border-[var(--cpm-warning-border)] !no-underline hover:!border-[var(--cpm-warning-icon)]"
                                style={{ gap: 12, padding: '11px 14px' }}
                            >
                                <span
                                    className="tabular !text-[var(--cpm-warning-text)]"
                                    style={{ fontSize: 16, fontWeight: 800, minWidth: 56 }}
                                >
                                    {formatCpmCount(usage)}
                                </span>
                                <span
                                    className="flex-1 !text-[var(--cpm-warning-text)]"
                                    style={{ fontSize: 12.5, fontWeight: 700 }}
                                >
                                    StateProduct rows in {stateName} still reference &ldquo;
                                    {category.category}&rdquo;
                                </span>
                            </Link>
                            <span
                                style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    lineHeight: 1.5,
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                Reassign those rows to another category in this state first, or
                                disable this category instead — disabling keeps its history and
                                stops it being used going forward.
                            </span>
                        </div>
                    ) : null}

                    <div
                        className="flex items-center border-t-[1.5px] border-[var(--cpm-border-strong)]"
                        style={{
                            gap: 10,
                            padding: '18px 24px',
                            marginTop: 14,
                            background: 'rgba(249,249,252,0.88)',
                        }}
                    >
                        <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                            <CpmButton
                                variant="outline"
                                size="modal"
                                onClick={finish}
                                disabled={working}
                            >
                                {blocked ? 'Back to the list' : 'Cancel'}
                            </CpmButton>
                            {blocked ? (
                                <CpmButton
                                    variant="warn"
                                    size="modal"
                                    onClick={() => void run(onDisableInstead, true)}
                                    isLoading={working}
                                    className="!bg-[var(--cpm-warning-text)] !text-white !border-transparent hover:!brightness-110"
                                >
                                    Disable instead
                                </CpmButton>
                            ) : (
                                <CpmButton
                                    variant="danger"
                                    size="modal"
                                    onClick={() => void run(onConfirmDelete, false)}
                                    isLoading={working}
                                >
                                    Delete category
                                </CpmButton>
                            )}
                        </div>
                    </div>
                </>
            )}
        </CpmModal>
    );
}
